import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Palette, Brush, Feather, Heart, Users, Award, CalendarDays, CheckCircle2, Play } from "lucide-react";
import { IMG } from "../components/brand";
import api, { API } from "../lib/api";
import { useImages } from "../lib/images";
import Seo from "../components/Seo";

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [currImages, setCurrImages] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const { images: IMG } = useImages();
  useEffect(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/public/curriculum/images").then((r) => setCurrImages(r.data)).catch(() => {});
    api.get("/public/gallery").then((r) => setGalleryPhotos(r.data)).catch(() => {});
  }, []);
  const price = settings?.course_price ?? 405;

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        description="Relaxed, expert-led adult art classes in East Croydon, South London. Learn drawing, watercolours, oil pastels and acrylics in a supportive 14-week course. Beginners welcome."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "14-week Adult Art Course — Seasons Art Class East Croydon",
          "description": "Drawing, oil pastels, watercolours and acrylics taught over 14 weekly three-hour sessions in East Croydon, South London.",
          "provider": { "@type": "Organization", "name": "Seasons Art Class East Croydon", "sameAs": "https://www.artclassesineastcroydon.co.uk/" },
          "offers": { "@type": "Offer", "price": price, "priceCurrency": "GBP", "availability": "https://schema.org/InStock" }
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* cool painterly blobs */}
        <div className="painterly-blob slow-spin" style={{ width: 520, height: 520, background: "radial-gradient(circle, #8EC9C7, transparent)", top: -160, left: -140, opacity: 0.55 }} />
        <div className="painterly-blob slow-spin" style={{ width: 420, height: 420, background: "radial-gradient(circle, #D0B4D8, transparent)", bottom: -180, right: -120, opacity: 0.55, animationDirection: 'reverse' }} />
        {/* floating paint droplets */}
        <svg className="paint-float absolute top-20 right-[46%] hidden md:block" width="34" height="34" viewBox="0 0 40 40"><path d="M20 3 C10 18 6 24 10 30 C14 36 26 36 30 30 C34 24 30 18 20 3Z" fill="#8EC9C7" opacity="0.75"/></svg>
        <svg className="paint-float delay-1 absolute top-56 right-[4%] hidden md:block" width="22" height="22" viewBox="0 0 40 40"><path d="M20 3 C10 18 6 24 10 30 C14 36 26 36 30 30 C34 24 30 18 20 3Z" fill="#A685BD" opacity="0.7"/></svg>
        <svg className="paint-float delay-2 absolute bottom-24 left-[42%] hidden md:block" width="28" height="28" viewBox="0 0 40 40"><path d="M20 3 C10 18 6 24 10 30 C14 36 26 36 30 30 C34 24 30 18 20 3Z" fill="#5D2C8F" opacity="0.55"/></svg>
        <svg className="paint-float delay-3 absolute top-[38%] left-[6%] hidden md:block" width="18" height="18" viewBox="0 0 40 40"><path d="M20 3 C10 18 6 24 10 30 C14 36 26 36 30 30 C34 24 30 18 20 3Z" fill="#8EC9C7" opacity="0.7"/></svg>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 z-10">
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 text-[#5D2C8F] text-sm border border-[#E8E2D5] mb-6" data-testid="home-enrolling-chip">
              <span className="h-1.5 w-1.5 rounded-full bg-[#8EC9C7]" /> Enrolling now — {settings?.current_term || "our next term"}
            </div>
            <h1 className="font-display text-[44px] sm:text-[52px] md:text-[60px] lg:text-[68px] leading-[1.05] tracking-tight text-[#5D2C8F]">
              The Art Class<br/>
              <span className="italic font-normal">You Always</span>{" "}
              <span className="hero-gradient-text">Wanted.</span>
            </h1>
            <p className="mt-6 text-base sm:text-[17px] text-[#5A4A6B] max-w-xl leading-relaxed">
              A warm, step-by-step 14-week art course for adult beginners and improvers in East Croydon.
              Drawing, Oil Pastels, Watercolours, Acrylics — and a new set of friends.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <Link to="/contact" className="sac-btn-primary" data-testid="hero-enquire-btn">
                Enquire about a place <ArrowRight size={18} />
              </Link>
              <button onClick={() => setVideoOpen(true)} className="sac-btn-secondary" data-testid="hero-video-btn">
                <Play size={16} /> Watch the 2-min intro
              </button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#8A6DA3]">
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#8EC9C7]" /> 14 weeks</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#8EC9C7]" /> Materials included</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#8EC9C7]" /> from £{price}</span>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative grid grid-cols-2 gap-4">
              <div className="overflow-hidden rounded-3xl aspect-[4/5] w-full col-span-2 lg:col-span-1 shadow-lg">
                <img src={IMG.hero} alt="Adults painting in class" className="ken-burns w-full h-full object-cover" />
              </div>
              <div className="flex flex-col gap-4">
                <div className="overflow-hidden rounded-3xl aspect-square shadow-lg mt-8">
                  <img src={IMG.watercolor} alt="Watercolour" className="ken-burns w-full h-full object-cover" style={{ animationDelay: '-6s' }} />
                </div>
                <div className="overflow-hidden rounded-3xl aspect-square shadow-lg">
                  <img src={IMG.oilPastel} alt="Oil pastel" className="ken-burns w-full h-full object-cover" style={{ animationDelay: '-12s' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-center mb-14">
          <div className="font-hand text-[#A685BD] text-2xl">four mediums · one journey</div>
          <h2 className="font-display text-4xl md:text-5xl text-[#5D2C8F] mt-2 brush-underline">Our four modules</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { title: "Drawing", img: IMG.drawing, icon: Feather, blurb: "Fundamentals of mark-making, tone and composition. The bedrock of every artist." },
            { title: "Oil Pastels", img: IMG.oilPastel, icon: Palette, blurb: "A delightful stepping stone — learn blending, colour theory, rich pigment." },
            { title: "Watercolours", img: IMG.watercolor, icon: Brush, blurb: "Luminous, portable, expressive. Master washes, wet-on-wet and layering." },
            { title: "Acrylics", img: IMG.acrylic, icon: Heart, blurb: "Our finale — impasto, palette knives, abstract and landscape techniques." },
          ].map((m) => (
            <div key={m.title} className="soft-card p-5 hover:-translate-y-1 transition-transform" data-testid={`module-card-${m.title.toLowerCase().replace(' ','-')}`}>
              <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4">
                <img src={m.img} alt={m.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
              </div>
              <div className="flex items-center gap-2 text-[#A685BD] mb-2"><m.icon size={18} /><span className="text-xs uppercase tracking-widest">Module</span></div>
              <div className="font-display text-2xl text-[#5D2C8F]">{m.title}</div>
              <p className="text-sm text-[#5A4A6B] mt-2 leading-relaxed">{m.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VIDEO STRIP */}
      <section className="relative bg-[#E6F0EF] py-20 overflow-hidden border-y border-[#CFE4E2]">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(600px circle at 20% 40%, rgba(142,201,199,0.7), transparent), radial-gradient(500px circle at 80% 60%, rgba(208,180,216,0.6), transparent)' }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-hand text-[#5D2C8F] text-2xl">a peek inside the studio</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#5D2C8F] mt-1 brush-underline">See our class in motion</h2>
            <p className="text-[#5A4A6B] mt-5 leading-relaxed">
              Two minutes to meet our founder Gen, hear from students across the country,
              and get a feel for what your Tuesday morning (or Friday evening) could look like.
            </p>
          </div>
          <button onClick={() => setVideoOpen(true)} className="group relative block aspect-video rounded-3xl overflow-hidden shadow-xl" data-testid="home-video-play-btn">
            <img src={IMG.tutor} alt="Intro video" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-[#5D2C8F]/30 group-hover:bg-[#5D2C8F]/20 transition-colors" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="h-20 w-20 rounded-full bg-white/95 flex items-center justify-center text-[#5D2C8F] shadow-2xl group-hover:scale-110 transition-transform">
                <Play size={28} fill="currentColor" />
              </span>
            </span>
          </button>
        </div>
      </section>

      {/* PHOTO MARQUEE */}
      <section className="py-16 overflow-hidden">
        <div className="text-center mb-8">
          <div className="font-hand text-[#A685BD] text-2xl">from our studio</div>
          <h2 className="font-display text-3xl md:text-4xl text-[#5D2C8F] mt-1">In the studio this term</h2>
        </div>
        <div className="relative">
          <div className="marquee-track flex gap-5 w-[200%]">
            {[...Array(2)].flatMap((_, rep) => {
              const galleryUrls = galleryPhotos.slice(0, 16).map((p) => `${API}/public/photos/${p.id}/image.jpg?size=600`);
              const sources = galleryUrls.length > 0
                ? galleryUrls
                : (currImages.length > 0
                    ? currImages.map((x) => x.src)
                    : [IMG.hero, IMG.watercolor, IMG.oilPastel, IMG.drawing, IMG.acrylic, IMG.tutor, IMG.studioA, IMG.studioB]);
              return sources.map((src, i) => (
                <div key={`${rep}-${i}`} className="shrink-0 w-72 aspect-[4/5] rounded-2xl overflow-hidden shadow-md">
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </div>
              ));
            })}
          </div>
        </div>
      </section>

      {/* SCHEDULE STRIP */}
      <section className="bg-[#EEE7DA] py-16 border-y border-[#E8E2D5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-hand text-[#8EC9C7] text-2xl">weekly, for 14 weeks</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#5D2C8F] mt-1 brush-underline">Pick your session</h2>
            <p className="text-[#5A4A6B] mt-5 max-w-md leading-relaxed">
              Each class is a warm, engaging three-hour session. The studio opens ten minutes early —
              time for a cuppa and a chat.
            </p>
            <Link to="/schedule" className="sac-btn-primary mt-7" data-testid="home-schedule-btn">
              See the schedule <ArrowRight size={18} />
            </Link>
          </div>
          <div className="space-y-4">
            {[
              { label: "Tuesday Morning" },
              { label: "Tuesday Afternoon" },
              { label: "Friday Evening" },
            ].map((s) => (
              <div key={s.label} className="soft-card px-6 py-5 flex items-center hover:-translate-y-1 transition-transform">
                <div className="h-12 w-12 rounded-2xl bg-[#8EC9C7]/30 flex items-center justify-center text-[#5D2C8F] mr-4"><CalendarDays size={22} /></div>
                <div className="font-display text-xl text-[#5D2C8F]">{s.label}</div>
              </div>
            ))}
            <div className="text-center pt-3 text-[#5A4A6B]">
              <span className="font-display italic text-[#5D2C8F] text-xl">From £{price}</span>
              <span className="text-sm"> · 14-week term</span>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="font-hand text-[#A685BD] text-2xl mb-4">from our founder, Gen</div>
        <p className="font-display italic text-2xl md:text-3xl text-[#5D2C8F] leading-snug">
          “It’s more than ‘just’ an art class. It’s your new hobby, it’s your new set of friends
          and local support network. It’s your favourite day of the week.”
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#5D2C8F] via-[#6E3AA0] to-[#3F1E66] p-12 lg:p-16 text-white">
          <div className="painterly-blob slow-spin" style={{ width: 480, height: 480, background: 'radial-gradient(circle, #8EC9C7, transparent)', top: -180, right: -160, opacity: 0.35 }} />
          <div className="relative z-10 max-w-2xl">
            <div className="font-hand text-[#8EC9C7] text-2xl">there’s a seat at the easel for you</div>
            <h3 className="font-display text-4xl md:text-5xl mt-2">Come and paint with us in East Croydon.</h3>
            <p className="mt-5 text-white/85 leading-relaxed">
              Spaces fill quickly each term from just £{price}. Pop us a quick enquiry and we’ll send the welcome pack, curriculum, and a friendly chat.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#8EC9C7] hover:bg-[#6DB6B4] text-[#5D2C8F] px-8 py-3 rounded-full font-semibold mt-7 transition-transform hover:-translate-y-1" data-testid="home-cta-enquire-btn">
              Start your enquiry <ArrowRight size={18} />
            </Link>
          </div>
          <Users size={240} className="absolute -right-10 -bottom-16 text-white/10" />
          <Award size={200} className="absolute right-40 -top-10 text-white/10" />
        </div>
      </section>

      {/* Video modal */}
      {videoOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setVideoOpen(false)} data-testid="home-video-modal">
          <button onClick={() => setVideoOpen(false)} className="absolute top-6 right-6 text-white text-3xl leading-none">×</button>
          <div className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <iframe
              title="Seasons Art Class intro"
              className="w-full h-full"
              src="https://www.youtube.com/embed/8xJWBdlXeLA?autoplay=1&rel=0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
